import { useMemo } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { PayloadToken } from "@/types/ai-prompt";
import { parseTokensFromText, tokenBadgeClass, statusLabel } from "./utils";

type PromptTokenBadgesProps = {
  promptText: string;
  payloadTokens: PayloadToken[];
};

export function PromptTokenBadges({ promptText, payloadTokens }: PromptTokenBadgesProps) {
  const foundKeys = useMemo(() => parseTokensFromText(promptText), [promptText]);
  const matchedTokens = useMemo(
    () =>
      foundKeys
        .map((k) => {
          const token = payloadTokens.find((t) => t.key === k);
          return token ? { key: k, token } : null;
        })
        .filter((item): item is { key: string; token: PayloadToken } => item !== null),
    [foundKeys, payloadTokens]
  );

  if (matchedTokens.length === 0) return null;

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex flex-wrap gap-1 mt-2">
        {matchedTokens.map(({ key, token }) => (
          <Tooltip key={key}>
            <TooltipTrigger asChild>
              <span
                className={cn(
                  "inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono border cursor-default",
                  tokenBadgeClass(token.status)
                )}
              >
                {"{"}
                {key}
                {"}"}
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-xs">
              <p className="font-medium">{token.description}</p>
              <p className="text-muted-foreground mt-0.5">
                Source: {token.data_source} &middot; {statusLabel(token.status)}
              </p>
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
    </TooltipProvider>
  );
}
